// @refresh reset
import React, { SetStateAction, useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { isEqual } from "lodash"
import { DimensionsContext, DimensionsProvider } from "../../../../../../../../Providers/DimensionsProvider"
import { D3EEGMontage } from "../D3/D3EEGMontage"
import { useRecoilState, useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { eegMontageConfigAtom, sharedEEGMontageHiddenChannelsAtom } from "../../../../Atoms/EEGMontage"
import { annotationsAtom, canOpenAnnotationsModalAtom, annotationZOrderAtom } from "../../../../Atoms/Annotations"
import { Annotation } from "../../../../../../../../Managers/VisualizationManager/Variables/Annotations"
import { useInProgressAnnotation } from "../../TimeSeriesGraphGroup/React/useInProgressAnnotation"
import { EEGWindowSettings } from "../../../../../../../../Managers/VisualizationManager/WindowSettings/EEGWindowSettings"
import { EEGMontageConfig, EEGMontageConfigJSON } from "../../../../Types/EEGMontageConfig"
import { selectedLayoutIdAtom } from "../../../../Atoms/Layout"
import { useD3KeyboardShortcuts } from "../../../../Hooks/useD3KeyboardShortcuts"
import { hotkeyActions } from "../../../../Types/KeyboardShortcut"
import { useD3CheckReloadData } from "../../../../Hooks/useD3CheckReloadData"
import { EEGTimeSeriesPageManager } from "../../../../Data/EEGTimeSeriesPageManager"
import { pageManagerRegistry } from "../../../../Data/PageManagerRegistry"
import { viewScaleRegistry } from "../../../../Data/ViewScaleRegistry"
import { useD3UpdateVisibleTraces } from "../../../../Hooks/useD3UpdateVisibleTraces"
import { fileScaleRegistry } from "../../../../Data/FileScaleRegistry"
import { useValidateEEGMontage } from "../../../../Hooks/useValidateEEGMontage"
import { getWindowMontage } from "../../../../Utils/eegWindowMontage"
import { useLayoutService } from "../../../../../../../../Hooks/useLayoutService"
import { useD3Controller } from "../../../../Hooks/useD3Controller"
import { useStableCallback } from "../../../../Hooks/useStableCallback"
import { useSocketProvider } from "../../../../../../../../Providers/SocketProvider"
import { useEventReviewProvider } from "../../../../../../../../Providers/EventReviewProvider"
import { VisualizationComponent } from "../../../../VisualizationComponent"
import { useEEGCalibration } from "../../../../Calibration/hooks"
import { EEGCalibrationStatus, CalibrationTokenPrefix } from "../../../../Calibration/types"
import { useAnnotationContextMenu } from "../../../ContextMenus/useAnnotationContextMenu"
import { useChannelVisibilityContextMenu } from "../../../ContextMenus/useChannelVisibilityContextMenu"
import { DataSource } from "../../../../Types/DataSource"
import { EEG_PAGING_SPEED_OPTIONS, EEGDisplayConfig, isResolvableEEGLead } from "../../../../Types/EEG"

type EEGMontageProps = {
	json: EEGMontageConfigJSON
	area: string
}

export function EEGMontageVisualization(props: EEGMontageProps) {
	const { montageMapping } = useRecoilValue(currentPatientFileInfoAtom)
	const layoutId = useRecoilValue(selectedLayoutIdAtom)
	const visualizationId = { layoutId: layoutId as string, windowId: props.area }
	const atom = eegMontageConfigAtom(visualizationId)
	const [config, setRootConfig] = useRecoilState(atom)
	const { modifyDatabaseWindowProperty } = useLayoutService()
	const montage = getWindowMontage(config.hasInitialized, config.montage, props.json.montage)

	// Auto-select montage if null - must run before channel validation check
	useValidateEEGMontage({
		montage,
		updateMontage: newMontage => {
			// Update the window state (allow initial montage selection even if not initialized)
			setRootConfig(previous => ({ ...previous, montage: newMontage }))

			// Update the layout only if already initialized (to avoid unnecessary DB calls on initial mount)
			if (config.hasInitialized) {
				modifyDatabaseWindowProperty("montage", visualizationId.layoutId, visualizationId.windowId, newMontage)
			}
		}
	})

	// Differentiate between two empty states:
	// 1. Patient doesn't have EEG data at all
	// 2. Patient has EEG but selected montage doesn't have valid channels
	const patientHasEEG = montageMapping.size > 0
	const hasValidChannels = (montage?.channels.filter(
		({ first, second }) => (
			isResolvableEEGLead(first, montageMapping) &&
			isResolvableEEGLead(second, montageMapping)
		)
	).length ?? 0) > 0

	// Determine which message to show (null means render visualization)
	let emptyStateMessage: string | null = null
	if (!patientHasEEG) {
		emptyStateMessage = "This recording doesn't contain EEG"
	} else if (!hasValidChannels) {
		emptyStateMessage = "No channels available for the selected montage"
	}

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<div style={{ flex: 0 }}>
				<EEGWindowSettings visualizationArea={props.area} montage={montage} />
			</div>
			<div style={{ flex: 1 }}>
				{emptyStateMessage === null ? (
					<DimensionsProvider>
						<EEGMontageD3Wrapper {...props} />
					</DimensionsProvider>
				) : (
				<div style={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "var(--color-chart-axis-text)"
				}}>
						{emptyStateMessage}
					</div>
				)}
			</div>
		</div>
	)
}

function EEGMontageD3Wrapper(props: EEGMontageProps) {
	const dimensions = useContext(DimensionsContext)
	const eventReviewProvider = useEventReviewProvider()
	const { inProgressAnnotation } = useInProgressAnnotation(props.area)
    const domNode = useRef<HTMLDivElement>(null)
    const { fileStartDate, fileEndDate, montageMapping, dataSourceMap } = useRecoilValue(currentPatientFileInfoAtom)
	const { getDataQuerySocket } = useSocketProvider()

	const annotations = useRecoilValue<Annotation[]>(annotationsAtom)
	const enableOpeningAnnotationsModal = useRecoilValue(canOpenAnnotationsModalAtom)
	const annotationZOrder = useRecoilValue(annotationZOrderAtom(props.area))
	const layoutId = useRecoilValue(selectedLayoutIdAtom)
	const visualizationId = { layoutId: layoutId as string, windowId: props.area }
	const atom = eegMontageConfigAtom(visualizationId)

	const [config, setRootConfig] = useRecoilState(atom)
	const montageId = config.montage?.id ?? null

	const montageKey = useMemo(() => {
		if (montageId !== null) {
			return `montage-${montageId}`
		}

		return `window-${props.area}`
	}, [montageId, props.area])

	const dataObjectId = dataSourceMap.get(DataSource.CURRENT_PATIENT) ?? null
	const sharedHiddenChannelsKey = useMemo(() => ({
		dataObjectId,
		montageKey,
	}), [dataObjectId, montageKey])

	const [sharedHiddenChannelKeys, setSharedHiddenChannelKeys] = useRecoilState(
		sharedEEGMontageHiddenChannelsAtom(sharedHiddenChannelsKey)
	)

	const updateSharedHiddenChannels = useCallback((hiddenKeys: SetStateAction<Set<string>>) => {
		setSharedHiddenChannelKeys(previous => {
			const nextHiddenKeys = typeof hiddenKeys === "function" ? hiddenKeys(previous) : hiddenKeys
			if (isEqual(previous, nextHiddenKeys)) {
				return previous
			}

			return new Set(nextHiddenKeys)
		})
	}, [setSharedHiddenChannelKeys])

	const onHiddenChannelsChange = useStableCallback(updateSharedHiddenChannels)

	const viewScale = viewScaleRegistry.get(visualizationId, VisualizationComponent.EEG_MONTAGE, { fileStartDate, fileEndDate, viewDuration: props.json.viewDuration })
	const [viewStart, viewEnd] = viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()
	const { status: calibrationStatus, pxPerMm } = useEEGCalibration()
	const { AnnotationContextMenu, openAnnotationContextMenu } = useAnnotationContextMenu(props.area)

	const eegConfig = useMemo(() => {
		return new EEGDisplayConfig(config.montage, config.LFF, config.HFF, config.notch, config.sensitivityMicroVolts, montageMapping)
	}, [config.HFF, config.LFF, config.montage, config.notch, config.sensitivityMicroVolts, montageMapping])

	const { ChannelVisibilityContextMenu, openChannelContextMenu } = useChannelVisibilityContextMenu({
		channels: eegConfig.channelDisplayInfo,
		hiddenChannelKeys: sharedHiddenChannelKeys,
		onHiddenChannelsChange: updateSharedHiddenChannels,
	})

	const calibration = useMemo(() => {
		if (calibrationStatus === EEGCalibrationStatus.VALID && pxPerMm > 0) {
			return {
				status: calibrationStatus,
				pxPerMm,
				token: `${CalibrationTokenPrefix.Valid}-${pxPerMm.toFixed(6)}`,
			}
		}

		return {
			status: calibrationStatus,
			token: `${CalibrationTokenPrefix.Status}-${calibrationStatus}`,
		}
	}, [calibrationStatus, pxPerMm])

	const d3Montage = useD3Controller<D3EEGMontage, EEGMontageConfig, EEGMontageConfigJSON, EEGTimeSeriesPageManager>({
		atom: atom,
		nodeRef: domNode,
		pageManager: pageManagerRegistry.get(visualizationId, VisualizationComponent.EEG_MONTAGE, new EEGTimeSeriesPageManager(), getDataQuerySocket),
		initProps: {
			...props.json,
			// A montage chosen or validated before the first mount must survive initialization.
			montage: config.montage ?? props.json.montage,
			playbackSpeed: EEG_PAGING_SPEED_OPTIONS.includes(props.json.playbackSpeed) ? props.json.playbackSpeed : 1,
		},
		props: {
			id: props.area,
			windowType: VisualizationComponent.EEG_MONTAGE,
			viewScale,
			fileScale: fileScaleRegistry.get(visualizationId, VisualizationComponent.EEG_MONTAGE, fileStartDate, fileEndDate),
			annotations,
			inProgressAnnotation,
			montageMapping,
			enableOpeningAnnotationsModal,
			currentlyReviewedAnnotation: eventReviewProvider.currentAnnotation,
			hideAnnotations: eventReviewProvider.hideAnnotations,
			annotationZOrder,
			calibration,
			hiddenChannelKeys: sharedHiddenChannelKeys,
		},
		d3ControllerConstructor(node, config, pageManager, callbacks) {
			return new D3EEGMontage(node, config, pageManager, { ...callbacks, onHiddenChannelsChange, openAnnotationContextMenu, openChannelContextMenu, setSelectedAnnotation: () => {} })
		},
		extractJSON(config) {
			return {
				viewDuration: viewDuration,
				playbackSpeed: config.playbackSpeed,
				targetMmPerSecond: config.targetMmPerSecond,
				montage: config.montage,
				LFF: config.LFF,
				HFF: config.HFF,
				notch: config.notch,
				sensitivityMicroVolts: config.sensitivityMicroVolts,
				ecgSensitivity: config.ecgSensitivity,
				isLinked: config.isLinked
			}
		},
	})

	useD3CheckReloadData({
		d3Controller: d3Montage,
		clearDataIfChanges: {
			viewDuration,
			LFF: config.LFF,
			HFF: config.HFF,
			channels: config.montage?.channels.map(({ first, second, type, ecgBasename }) => `${type ?? "eeg"}:${first}-${second}:${ecgBasename ?? ""}`),
			notch: config.notch,
			dimensions
		},
		clearRenderCacheIfChanges: {
			sensitivityMicroVolts: config.sensitivityMicroVolts,
			ecgSensitivity: config.ecgSensitivity,
			calibration: calibration.token,
		}
	})

	useD3KeyboardShortcuts({
		d3Controller: d3Montage,
		windowId: props.area,
		shortcuts: [
			hotkeyActions.NEXT_PAGE,
			hotkeyActions.PREVIOUS_PAGE,
			hotkeyActions.UPDATE_CUSTOM_ANNOTATION_MARKERS,
			hotkeyActions.CANCEL_ANNOTATION,
			hotkeyActions.PLAY_PAUSE
		]
	})

	useD3UpdateVisibleTraces({
		d3Controller: d3Montage,
		windowId: props.area
	})

	useEffect(() => {
		setRootConfig(previous => {
			if (isEqual(previous.hiddenChannelKeys, sharedHiddenChannelKeys)) {
				return previous
			}

			return { ...previous, hiddenChannelKeys: new Set(sharedHiddenChannelKeys) }
		})
	}, [setRootConfig, sharedHiddenChannelKeys])

	return (
		<div ref={domNode} style={{ height: "100%" }}>
			{AnnotationContextMenu}
			{ChannelVisibilityContextMenu}
		</div>
	)
}
